import { Injectable, signal } from '@angular/core';
import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  onIdTokenChanged,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import { environment } from '../../../environments/environment';

export const EMAIL_NOT_VERIFIED_ERROR = 'EMAIL_NOT_VERIFIED';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth: Auth;

  user = signal<User | null>(null);
  token = signal<string | null>(null);
  isLoading = signal<boolean>(true);

  private authLoadedPromise: Promise<User | null>;
  private resolveAuthLoaded!: (user: User | null) => void;

  constructor() {
    const app = !getApps().length ? initializeApp(environment.firebaseConfig) : getApp();
    this.auth = getAuth(app);

    this.authLoadedPromise = new Promise((resolve) => {
      this.resolveAuthLoaded = resolve;
    });

    const setupListener = async () => {
      try {
        await setPersistence(this.auth, browserLocalPersistence);
      } catch (e) {
        console.error(e);
      }

      let firstResolve = true;
      onIdTokenChanged(this.auth, async (user) => {
        if (
          user &&
          !user.emailVerified &&
          user.providerData.some((p) => p.providerId === 'password')
        ) {
          // If email password user is not verified, do not treat as logged in
          this.user.set(null);
          this.token.set(null);
        } else {
          this.user.set(user);
          if (user) {
            const idToken = await user.getIdToken();
            this.token.set(idToken);
          } else {
            this.token.set(null);
          }
        }
        this.isLoading.set(false);

        if (firstResolve) {
          firstResolve = false;
          this.resolveAuthLoaded(this.user());
        }
      });
    };
    setupListener();
  }

  async ensureAuthLoaded(): Promise<User | null> {
    if (!this.isLoading()) {
      return this.user();
    }
    return this.authLoadedPromise;
  }

  async loginWithEmail(email: string, pass: string): Promise<void> {
    const cred = await signInWithEmailAndPassword(this.auth, email, pass);
    if (cred.user && !cred.user.emailVerified) {
      // Send verification email automatically and log out
      try {
        await sendEmailVerification(cred.user);
      } catch (e) {
        console.warn('Could not re-send verification email:', e);
      }
      await signOut(this.auth);
      throw new Error(EMAIL_NOT_VERIFIED_ERROR);
    }
  }

  async registerWithEmail(email: string, pass: string, name?: string): Promise<void> {
    const cred = await createUserWithEmailAndPassword(this.auth, email, pass);
    if (name && cred.user) {
      await updateProfile(cred.user, { displayName: name });
    }
    if (cred.user) {
      await sendEmailVerification(cred.user);
      await signOut(this.auth);
    }
  }

  async loginWithGoogle(): Promise<void> {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(this.auth, provider);
  }

  async resetPassword(email: string): Promise<void> {
    await sendPasswordResetEmail(this.auth, email);
  }

  async logout(): Promise<void> {
    await signOut(this.auth);
  }

  async getFreshToken(): Promise<string | null> {
    try {
      const currentUser = this.auth.currentUser;
      if (currentUser) {
        const newToken = await currentUser.getIdToken(true);
        this.token.set(newToken);
        return newToken;
      }
    } catch (error) {
      console.error('Failed to refresh Firebase token:', error);
    }
    this.token.set(null);
    return null;
  }
}
